import React, { useEffect, useState, useCallback } from "react";
import {
  authFetch,
  clearAuthTokens,
  logoutUser,
} from "../../utils/auth";
import { Link, useNavigate } from "react-router-dom";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";

import "./Admin.css";
import "./AdminBlog.css";
import "./AdminBlog.mobile.css";

// ── Barre d'outils ────────────────────────────────────────

const Toolbar = ({ editor, onImageUpload }) => {
  if (!editor) return null;

  const btn = (action, label, title, isActive = false) => (
    <button
      type="button"
      className={`tb-btn ${isActive ? "tb-btn--active" : ""}`}
      onClick={action}
      title={title}
    >
      {label}
    </button>
  );

  return (
    <div className="editor-toolbar">

      <div className="tb-group">
        {btn(
          () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
          "H1",
          "Titre principal",
          editor.isActive("heading", { level: 1 })
        )}

        {btn(
          () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
          "H2",
          "Titre secondaire",
          editor.isActive("heading", { level: 2 })
        )}

        {btn(
          () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
          "H3",
          "Titre tertiaire",
          editor.isActive("heading", { level: 3 })
        )}
      </div>

      <div className="tb-sep" />

      <div className="tb-group">
        {btn(
          () => editor.chain().focus().toggleBold().run(),
          "G",
          "Gras (Ctrl+B)",
          editor.isActive("bold")
        )}

        {btn(
          () => editor.chain().focus().toggleItalic().run(),
          "I",
          "Italique (Ctrl+I)",
          editor.isActive("italic")
        )}

        {btn(
          () => editor.chain().focus().toggleStrike().run(),
          "S̶",
          "Barré",
          editor.isActive("strike")
        )}

        {btn(
          () => editor.chain().focus().toggleCode().run(),
          "</>",
          "Code inline",
          editor.isActive("code")
        )}
      </div>

      <div className="tb-sep" />

      <div className="tb-group">
        {btn(
          () => editor.chain().focus().toggleBulletList().run(),
          "• Liste",
          "Liste à puces",
          editor.isActive("bulletList")
        )}

        {btn(
          () => editor.chain().focus().toggleOrderedList().run(),
          "1. Liste",
          "Liste numérotée",
          editor.isActive("orderedList")
        )}

        {btn(
          () => editor.chain().focus().toggleBlockquote().run(),
          "❝ Citation",
          "Citation",
          editor.isActive("blockquote")
        )}

        {btn(
          () => editor.chain().focus().setHorizontalRule().run(),
          "─── Séparation",
          "Séparation"
        )}
      </div>

      <div className="tb-sep" />

      <div className="tb-group">

        <label
          className="tb-btn tb-btn--image"
          title="Uploader une image"
        >
          ⬆ Image

          <input
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={onImageUpload}
          />
        </label>

        {btn(
          () => {
            const url = window.prompt("URL de l'image :");

            if (url) {
              editor
                .chain()
                .focus()
                .setImage({ src: url })
                .run();
            }
          },
          "🔗 URL image",
          "Insérer via URL"
        )}

      </div>

      <div className="tb-sep" />

      <div className="tb-group">
        {btn(
          () => editor.chain().focus().undo().run(),
          "↩",
          "Annuler (Ctrl+Z)"
        )}

        {btn(
          () => editor.chain().focus().redo().run(),
          "↪",
          "Refaire (Ctrl+Y)"
        )}
      </div>

    </div>
  );
};


// ── Composant principal ───────────────────────────────────

const AdminBlog = () => {

  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [published, setPublished] = useState(true);

  const [preview, setPreview] = useState(false);

  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const [loading, setLoading] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  const [autoSaveLabel, setAutoSaveLabel] = useState("");

  const navigate = useNavigate();

// ── Auth ───────────────────────────────────────────────
	useEffect(() => {
	const check = async () => {
		try {
			const res = await authFetch(
				"/api/dashboard/user/"
			);

			if (!res.ok) {
				throw new Error("Authentication failed");
			}

			setAuthChecked(true);

			} catch {
			clearAuthTokens();

			navigate(
				"/login",
				{ replace: true }
			);
			}
		};

		check();
	}, [navigate]);

  // ── Éditeur TipTap ─────────────────────────────────────

  const editor = useEditor({
    extensions: [
      StarterKit,

      Image.configure({
        inline: false,
        allowBase64: true,
      }),

      Placeholder.configure({
        placeholder: "Commencez à rédiger votre article ici…",
      }),
    ],

    content: "",

    editorProps: {
      attributes: {
        class: "tiptap-editor",
      },
    },
  });


  // ── Restauration brouillon ─────────────────────────────

  useEffect(() => {

    if (!editor) return;

    try {

      const saved = localStorage.getItem("adminblog_draft");

      if (saved) {

        const {
          title: t,
          slug: s,
          content: c,
        } = JSON.parse(saved);

        setTitle(t || "");
        setSlug(s || "");

        if (c) {
          editor.commands.setContent(c);
        }

        setAutoSaveLabel("Brouillon restauré");

      }

    } catch {}

  }, [editor]);


  // ── Auto-save ──────────────────────────────────────────

  useEffect(() => {

    if (!editor || !authChecked) return;

    const interval = setInterval(() => {

      localStorage.setItem(
        "adminblog_draft",
        JSON.stringify({
          title,
          slug,
          content: editor.getHTML(),
        })
      );

      setAutoSaveLabel(
        "Sauvegardé à " +
          new Date().toLocaleTimeString(
            "fr-FR",
            {
              hour: "2-digit",
              minute: "2-digit",
            }
          )
      );

    }, 30000);

    return () => clearInterval(interval);

  }, [
    editor,
    title,
    slug,
    authChecked,
  ]);


  // ── Upload image ───────────────────────────────────────

  const handleImageUpload = useCallback(
    (e) => {

      const file = e.target.files[0];

      if (!file || !editor) return;

      const reader = new FileReader();

      reader.onload = () => {
        editor
          .chain()
          .focus()
          .setImage({
            src: reader.result,
          })
          .run();
      };

      reader.readAsDataURL(file);

      e.target.value = "";

    },
    [editor]
  );


  // ── Slug auto ─────────────────────────────────────────

  const handleTitleChange = (e) => {

    const val = e.target.value;

    setTitle(val);

    setSlug(
      val
        .toLowerCase()
        .trim()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^\w\s-]/g, "")
        .replace(/[\s_-]+/g, "-")
        .replace(/^-+|-+$/g, "")
    );

  };


  // ── Logout ────────────────────────────────────────────

	const handleLogout = async () => {
		await logoutUser();
		navigate("/login", { replace: true });
	};


  // ── Soumission ────────────────────────────────────────

  const handleSubmit = async (e) => {

    e.preventDefault();

    if (!editor) return;

    setLoading(true);
    setError("");
    setMessage("");


    try {

		const res = await authFetch(
			"/api/blog/admin/articles/",
			{
				method: "POST",
				headers: {
				"Content-Type": "application/json",
				},
				body: JSON.stringify({
				title,
				slug,
				content: editor.getHTML(),
				published,
				}),
			}
		);

      if (!res.ok) {
        throw new Error("Erreur lors de l'envoi");
      }

      setMessage("Article publié avec succès !");

      setTitle("");
      setSlug("");

      editor.commands.clearContent();

      localStorage.removeItem("adminblog_draft");

      setAutoSaveLabel("");

    } catch (err) {

      setError(err.message);

    } finally {

      setLoading(false);

    }
  };


  if (!authChecked) {
    return null;
  }


  return (
    <div className="admblog-page">

      {/* ── HEADER ── */}

      <header className="adm-header">

        <div className="adm-header__left">

          <Link
            to="/adminHome"
            className="adm-header__back"
          >
            ← DASHBOARD
          </Link>

          <h1 className="adm-header__title">
            ADMIN
            <span className="adm-header__slash">
              /
            </span>
            ARTICLE
          </h1>

        </div>


        <div className="adm-header__right">

          {autoSaveLabel && (
            <span className="adm-header__user">
              {autoSaveLabel}
            </span>
          )}

          <button
            type="button"
            className={
              `admblog-preview-btn ${
                preview
                  ? "admblog-preview-btn--active"
                  : ""
              }`
            }
            onClick={() => setPreview(!preview)}
          >
            {preview ? "✎ ÉDITER" : "◉ APERÇU"}
          </button>

          <button
            className="adm-header__logout"
            onClick={handleLogout}
          >
            Déconnexion
          </button>

        </div>

      </header>


      <form
        onSubmit={handleSubmit}
        className="admblog-form"
      >

        {/* ── MÉTA ── */}

        <div className="admblog-meta">

          <div className="admblog-field admblog-field--title">

            <label className="admblog-label">
              TITRE
            </label>

            <input
              className="admblog-input admblog-input--title"
              type="text"
              value={title}
              onChange={handleTitleChange}
              placeholder="Titre de l'article"
              required
            />

          </div>


          <div className="admblog-field admblog-field--slug">

            <label className="admblog-label">
              SLUG
            </label>

            <input
              className="admblog-input"
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              required
            />

          </div>

        </div>


        {/* ── ÉDITEUR / APERÇU ── */}

        {preview ? (

          <div className="admblog-preview-panel">

            <div className="admblog-preview-inner">

              <div className="admblog-preview-label">
                APERÇU
              </div>

              <h2 className="admblog-preview-title">
                {title || "Sans titre"}
              </h2>

              <div
                className="admblog-preview-content"
                dangerouslySetInnerHTML={{
                  __html: editor?.getHTML() || "",
                }}
              />

            </div>

          </div>

        ) : (

          <div className="admblog-editor-wrap">

            <Toolbar
              editor={editor}
              onImageUpload={handleImageUpload}
            />

            <EditorContent
              editor={editor}
              className="admblog-editor-content"
            />

          </div>

        )}


        {/* ── FOOTER ── */}

        <div className="admblog-footer">

          <label className="admblog-checkbox">

            <input
              type="checkbox"
              checked={published}
              onChange={() => setPublished(!published)}
            />

            <span className="admblog-checkmark" />

            PUBLIER IMMÉDIATEMENT

          </label>


          <div className="admblog-footer-right">

            {message && (
              <span className="admblog-msg admblog-msg--success">
                ✓ {message}
              </span>
            )}

            {error && (
              <span className="admblog-msg admblog-msg--error">
                ✕ {error}
              </span>
            )}

            <button
              type="submit"
              className="admblog-submit"
              disabled={loading}
            >
              {loading
                ? "ENVOI..."
                : published
                  ? "PUBLIER →"
                  : "ENREGISTRER →"
              }
            </button>

          </div>

        </div>

      </form>

    </div>
  );
};

export default AdminBlog;